/**
 * Stabiler Device Fingerprint fuer Hotel TV Registrierung.
 *
 * Basis: serialNumber + mac + model — nur hardware-gebundene Werte.
 * Format: "<serialNumber>|<mac>|<model>" → SHA-256 (64 Hex-Zeichen)
 *
 * Eigenschaften:
 *   - Deterministisch: gleiche Hardware → immer gleicher Hash
 *   - Firmware-unabhaengig: kein version/timestamp/random
 *   - Einheitlich: alle Plattformen (Tizen, LG, Browser) → gleiche Struktur
 */

import Device from './Device';

class DeviceFingerprint {
  constructor() {
    this.fingerprintLength = 64;
  }

  /**
   * Gibt den SHA-256 Hash von "serialNumber|mac|model" zurueck (64 Hex-Zeichen).
   * Fallback auf simpleHash fuer alte TVs ohne crypto.subtle (z.B. WebOS 3.5).
   */
  async generateFingerprint() {
    try {
      const [serialNumber, mac, model] = await Promise.all([
        Device.serialNumber,
        Device.mac,
        Device.model,
      ]);

      // Einheitliches Format: pipe-separiert, Leerzeichen getrimmt, Grossbuchstaben
      const raw = [
        (serialNumber || '').trim().toUpperCase(),
        (mac || '').trim().toUpperCase(),
        (model || '').trim().toUpperCase(),
      ].join('|');

      console.log('[Fingerprint] raw:', raw);
      return await this.hash(raw);
    } catch (error) {
      console.warn('[Fingerprint] generation error:', error);
      return this.generateFallbackFingerprint();
    }
  }

  /**
   * SHA-256 Hash → 64 Hex-Zeichen.
   * Fallback auf simpleHash fuer TVs ohne crypto.subtle.
   */
  async hash(input) {
    try {
      if (window.crypto && window.crypto.subtle) {
        const encoder = new TextEncoder();
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', encoder.encode(input));
        return Array.from(new Uint8Array(hashBuffer))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');  // SHA-256 = genau 64 Hex-Zeichen
      }
      return this.simpleHash(input);
    } catch (error) {
      return this.simpleHash(input);
    }
  }

  /**
   * Einfacher deterministischer Hash als Fallback (WebOS 3.5, Orsay).
   * Gibt 64 Hex-Zeichen zurueck.
   */
  simpleHash(str) {
    let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
    for (let i = 0; i < str.length; i++) {
      const ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    const hex = (4294967296 + h1).toString(16).slice(-8) + (4294967296 + h2).toString(16).slice(-8);
    // Auf 64 Zeichen strecken: Hash + Hash des Strings-Laenge
    return (hex + hex + str.length.toString(16).padStart(16, '0') + hex).substring(0, this.fingerprintLength);
  }

  /**
   * Fallback wenn Device-Daten nicht abrufbar — deterministisch, kein random.
   * Nutzt UserAgent + Aufloesung als stabile Geraetemerkmale.
   */
  async generateFallbackFingerprint() {
    const raw = [
      navigator.userAgent.trim().toUpperCase(),
      String(screen.width),
      String(screen.height),
    ].join('|');
    return this.hash(raw);
  }
}

export default DeviceFingerprint;



// WEBPACK FOOTER //
// ./src/helpers/fingerprint.js